import { Injectable } from '@angular/core';
import { TfErrorModel } from '@app/models/tf-error.model';
import { PricingDetails, PricingDetailsModel, PricingModel, PricingOptionsModel, ThermofisherPricingApiService } from '@app/modules/tf-pricing';
import { catchError, map, Observable, of, Subject, throwError, zip } from 'rxjs';
import { PendingSkuInterface } from '@app/modules/tf-pricing';

@Injectable()
export class ThermofisherPricingService {

    serial: number = 0;

    pendingObservables: Record<number, PendingSkuInterface> = {};
    pricingDetailsRecord: Record<string, PricingModel> = {};

    constructor(
        private thermofisherPricingApiService: ThermofisherPricingApiService
    ) {
    }

    preloadPricingInfo(skus: string[], isLoggedIn: boolean): void {

        const uniqueSkus = this.filterUniqueSkus(skus);

        this.retrievePricingInfo(uniqueSkus, isLoggedIn).subscribe({
                next: () => {
                    this.processObservables(uniqueSkus);
                },
                error: () => {
                    this.processObservables(uniqueSkus);
                }
            }
        );

    }


    processObservables(skus: string[]): void {

        skus.forEach((sku) => {

            Object.entries(this.pendingObservables).forEach(([serial, pendingObservable]) => {
                if (pendingObservable.sku === sku) {
                    pendingObservable.subject.next(this.pricingDetailsRecord[pendingObservable.sku]);
                    pendingObservable.subject.complete();
                    delete this.pendingObservables[serial];
                }
            });

        });

    }

    processPricingDetails(pricingData: any): PricingDetails {

        const pricingList = pricingData.pricingList;

        let pricingDetailsModel: PricingDetails = {};

        if (pricingList
            && Array.isArray(pricingList.pricingDetails)
            && pricingList.pricingDetails.length > 0
            && (pricingList.pricingDetails[0].action === 'Add To Cart')
        ) {
            pricingDetailsModel = new PricingDetailsModel(pricingList.pricingDetails[0]);
            return pricingDetailsModel;
        }

        if (pricingList.pricingServiceError) {
            pricingDetailsModel.action = 'View price in cart';
            pricingDetailsModel.message = 'View price in cart';
            return pricingDetailsModel;
        }

        pricingDetailsModel.action = 'Price not available';
        pricingDetailsModel.message = 'Price not available';
        pricingDetailsModel.error = true;

        return pricingDetailsModel;

    }

    getPreloadedPricing(sku: string): Observable<PricingModel> {

        if (this.pricingDetailsRecord[sku]) {
            return of(this.pricingDetailsRecord[sku]);
        }

        const data: PendingSkuInterface = {
            sku: sku,
            subject: new Subject<PricingModel>()
        };

        this.pendingObservables[this.serial++] = data;

        return data.subject;

    }

    getPricing(sku: string, isLoggedIn: boolean): Observable<PricingModel> {

        if (this.pricingDetailsRecord[sku]) {
            return of(this.pricingDetailsRecord[sku]);
        }

        return this.retrievePricingInfo([sku], isLoggedIn).pipe(
            map(() => {
                return this.pricingDetailsRecord[sku];
            })
        );

    }

    retrievePricingInfo(skus: string[], isLoggedIn: boolean): Observable<any> {

        const chunkSize = 13;

        if (skus.length <= chunkSize) {
            return this.retrievePricingInfoAttempt(skus, isLoggedIn);
        }

        const obs = [];

        for (let i = 0; i < skus.length; i += chunkSize) {
            const chunk = skus.slice(i, i + chunkSize);
            if (chunk.length > 0) {
                obs.push(this.retrievePricingInfoAttempt(chunk, isLoggedIn))
            }
        }

        return zip(obs);

    }

    private filterUniqueSkus(skus: string[]): string[] {
        return skus.filter((item, pos, self) => {
            return self.indexOf(item) == pos;
        });
    }

    private retrievePricingInfoAttempt(uniqueSkus: string[], isLoggedIn: boolean): Observable<any> {

        const pricingOptions = new PricingOptionsModel({isLoggedIn: isLoggedIn});

        return this.thermofisherPricingApiService.retrievePrice(uniqueSkus.join(), pricingOptions).pipe(
            map((data) => {
                Object.keys(data['myPriceData']).forEach(sku => {

                    const pricingDetail = this.processPricingDetails(data['myPriceData'][sku]);
                    this.pricingDetailsRecord[sku] = new PricingModel({
                        catalogNumber: sku,
                        pricingDetails: pricingDetail,
                        pricingServiceError: pricingDetail.error
                    });

                });
                return data;

            }),
            catchError(() => {

                uniqueSkus.forEach((sku) => {

                    const pricingDetail = new PricingDetailsModel({
                        action: 'Price not available',
                        message: 'Price not available',
                        error: true
                    })

                    this.pricingDetailsRecord[sku] = new PricingModel({
                        catalogNumber: sku,
                        pricingDetails: pricingDetail,
                        pricingServiceError: true
                    });
                })
                return throwError(() => new TfErrorModel('PRICING_ERROR', 'Error fetching prices'));
            })
        );
    }

    getAnonymousCurrencyCode() {
        let currencyCode = null;
        for (const sku in this.pricingDetailsRecord) {
            if (this.pricingDetailsRecord[sku]?.pricingDetails?.pricingCurrencyCode != undefined) {
                currencyCode = this.pricingDetailsRecord[sku]?.pricingDetails?.pricingCurrencyCode;
                break;
            }
        }
        return currencyCode != null ? currencyCode : '';
    }

}
